/**
 * TestsHub — quiz session list + numQ selector + CTA.
 *
 * Design source: /tmp/arianna-design/ios-reader/project/tests.jsx TestsHub
 *
 * Narrative text uses buildQuizHubNarrative from @arianna/reader-core.
 * numQ options: [15, 20, 25, 30].filter(n < totalQuestions) + totalQuestions.
 */

import * as React from 'react'
import { useMemo } from 'react'
import { clsx } from 'clsx'
import Heading from '@arianna/design-system/Heading'
import Text from '@arianna/design-system/Text'
import { buildQuizHubNarrative } from '@arianna/reader-core'
import { CompletedCard } from './CompletedCard.js'
import { InProgressCard } from './InProgressCard.js'

// ---------------------------------------------------------------------------
// Session shape consumed by TestsHub
// ---------------------------------------------------------------------------

export interface TestsHubSession {
  id: string
  started_at: number
  completed_at: number | null
  num_questions: number
  current_idx: number
  /** Derived server-side (Quiz#score); null while the session is in progress. */
  score: { correct: number; wrong: number; skipped: number; total: number } | null
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface TestsHubProps {
  totalQuestions: number
  sessions: TestsHubSession[]
  /** Currently selected numQ (controlled by parent) */
  numQ: number
  onNumQChange: (n: number) => void
  onNewSession: () => void
  /** Called with the session ID (not the full session object) to avoid coupling to platform-specific session shapes. */
  onResumeSession: (sessionId: string) => void
  onViewResults: (sessionId: string) => void
  onDiscardSession: (sessionId: string) => void
}

const BASE_OPTIONS = [15, 20, 25, 30] as const

// ---------------------------------------------------------------------------
// TestsHub component
// ---------------------------------------------------------------------------

export function TestsHub({
  totalQuestions,
  sessions,
  numQ,
  onNumQChange,
  onNewSession,
  onResumeSession,
  onViewResults,
  onDiscardSession,
}: TestsHubProps) {
  // numQ pill options: base options smaller than total + total itself
  const qOptions = useMemo(
    () => [...BASE_OPTIONS.filter((n) => n < totalQuestions), totalQuestions],
    [totalQuestions],
  )

  const inProgress = sessions.find((s) => s.completed_at == null) ?? null
  const completed = useMemo(
    () =>
      sessions
        .filter((s) => s.completed_at != null)
        .sort((a, b) => (b.completed_at ?? 0) - (a.completed_at ?? 0)),
    [sessions],
  )

  // Narrative text via shared helper
  const narrative = useMemo(() => {
    const summaries = sessions.map((s) => ({
      completedAt: s.completed_at,
      score: s.score != null ? { correct: s.score.correct, total: s.score.total } : null,
    }))
    return buildQuizHubNarrative(summaries)
  }, [sessions])

  return (
    <div className="flex-1 flex flex-col bg-bg overflow-hidden">
      {/* TASK-014 dedup: no hub top-bar at all. The course shell's header +
          tabs already say where the user is, and the session count is
          readable from the narrative/storico below — a "Verifiche · N
          eseguite" bar duplicated both. */}
      {/* ── Scrollable body ───────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        {/* design handoff: maxWidth 880px, px 48px, pt 44px, pb 80px */}
        <div className="max-w-[880px] mx-auto px-12 pt-11 pb-[80px]">
          {/* Title block — stable H1 (the page's name, same shared DS variant
              as every course section) + the quiz narrative demoted to lead,
              mirroring Panoramica's greeting-plus-lead pattern exactly. */}
          <Heading variant="sectionCompact" as="h1">
            Verifiche
          </Heading>
          <Text variant="lead" className="block mt-2 max-w-[560px]">
            {narrative}
          </Text>

          {/* Action group — one visual unit: eyebrow, then pills + CTA on a
              single wrapping row so "choose length → start" reads as one
              gesture instead of two stacked blocks. */}
          <div className="mt-8">
            <div className="text-[10px] text-ink-3 tracking-[0.12em] uppercase font-semibold mb-[10px] font-sans">
              Numero di domande
            </div>
            <div className="flex items-center gap-[6px] flex-wrap">
              {qOptions.map((n) => {
                const active = numQ === n
                const label = n === totalQuestions ? `${n} · tutte` : String(n)
                return (
                  <button
                    key={n}
                    onClick={() => onNumQChange(n)}
                    className={clsx(
                      /* design handoff: py 7px, px 16px, borderRadius 999px */
                      'py-[7px] px-4 text-sm rounded-full cursor-pointer font-sans transition-colors duration-150',
                      active
                        ? 'font-semibold bg-ink text-bg border border-ink'
                        : 'font-medium bg-transparent text-ink-3 border border-line',
                    )}
                  >
                    {label}
                  </button>
                )
              })}

              {/* CTA — same row, pushed right; wraps under the pills on
                  narrow viewports. */}
              <div className="flex-1 min-w-[8px]" />
              <button
                onClick={onNewSession}
                className="inline-flex items-center gap-2 px-[22px] py-3 text-base font-semibold bg-ink text-bg border-none rounded-[10px] cursor-pointer font-sans whitespace-nowrap"
              >
                <span className="text-accent-soft">▶</span>
                {inProgress != null
                  ? `Nuova verifica · ${numQ} domande`
                  : `Avvia verifica · ${numQ} domande`}
              </button>
            </div>
          </div>

          {/* In-progress card — no section eyebrow: the card's own
              "IN SOSPESO" badge (with timestamp) already names it, and the
              two labels sat 30px apart saying the same thing. */}
          {inProgress != null && (
            <div className="mt-10">
              <InProgressCard
                session={inProgress}
                onResume={() => onResumeSession(inProgress.id)}
                onDiscard={() => onDiscardSession(inProgress.id)}
              />
            </div>
          )}

          {/* Completed sessions */}
          {completed.length > 0 && (
            <div className="mt-10">
              <div className="text-[10px] text-ink-3 tracking-[0.12em] uppercase font-semibold mb-3 font-sans">
                Storico · dal più recente
              </div>
              <div className="flex flex-col gap-[10px]">
                {completed.map((s, i) => (
                  <CompletedCard
                    key={s.id}
                    session={s}
                    isFirst={i === 0}
                    onView={() => onViewResults(s.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {sessions.length === 0 && (
            <div className="mt-10 px-7 py-6 bg-surface border border-line rounded-lg font-serif text-[16px] leading-[1.65] text-ink-2">
              Le verifiche che eseguirai qui verranno salvate. Potrai rivedere i risultati, capire
              gli errori e tracciare i progressi nel tempo.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
